import { defineComponent as _defineComponent } from 'vue'
import { ref, useRoute, computed } from '@nuxtjs/composition-api'
import { AppButton, ModalBotton } from '@shared/ui'
import { useMenuModel, createOrderByTable, getMenuStopList } from '@entities/menu'
import { useRestaurantModel } from '@entities/restaurant'
import { useMetaModel } from '@entities/meta'
import { useNotificationModel } from '@shared/model/notification'
import { useOrderModel } from '@entities/order'
import { useBrowserModel } from '@entities/browser'
import type {
  ICreateOrderPayload,
  ICreateOrderProductPayload,
  ICreateOrderModifierPayload
} from '@entities/menu/model/types'


export default /*#__PURE__*/_defineComponent({
  __name: 'MenuCartOrder',
  emits: ['goToOrder'],
  setup(__props, { emit }) {

const menuModel = useMenuModel()
const restaurantModel = useRestaurantModel()
const metaModel = useMetaModel()
const notificationModel = useNotificationModel()
const orderModel = useOrderModel()
const browserModel = useBrowserModel()
const route = useRoute()


const showConfirmModal = ref(false)
const isProcessing = ref(false)
const orderSuccess = ref(false)
const orderError = ref('')
const submittedCart = ref<any[]>([])
const shouldClearCartOnTransition = ref(false)
const showStopListModal = ref(false)
const unavailableDishes = ref<any[]>([])
const unavailableDishesOnly = ref<any[]>([]) // Только блюда, полностью в стоп-листе (без модификаторов)
const numberFormatter = new Intl.NumberFormat('ru-RU')

const getDishWord = (count: number) => {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) {
    return 'блюдо'
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return 'блюда'
  }
  return 'блюд'
}

const cartDishCount = computed(() => {
  return menuModel.cart
    .filter(item => !menuModel.unavailableDishIds.has(item.id))
    .reduce((acc, item) => acc + (item.inCart || 0), 0)
})

const cartTotalPrice = computed(() => {
  return menuModel.cart
    .filter(item => !menuModel.unavailableDishIds.has(item.id))
    .reduce((acc, item) => acc + ((item.price || 0) * (item.inCart || 0)), 0)
})

const cartSummaryText = computed(() => {
  if (!cartDishCount.value || !cartTotalPrice.value) {
    return ''
  }
  const dishesPart = `${cartDishCount.value} ${getDishWord(cartDishCount.value)}`
  const pricePart = `${numberFormatter.format(cartTotalPrice.value / 100)} ₽`
  return `${dishesPart} · ${pricePart}`
})

const DEFAULT_ERROR_MESSAGE = 'Не удалось оформить заказ. Попробуйте ещё раз.'
const MIN_PROCESSING_DURATION = 3000

const getModifiers = (item: any): any[] => {
  if (!item?.selectedModifiers || item.selectedModifiers.length === 0) {
    return []
  }

  const available: any[] = []
  if (Array.isArray(item.modifiers)) {
    available.push(...item.modifiers)
  }
  if (Array.isArray(item.modifier_groups)) {
    item.modifier_groups.forEach((group: any) => {
      if (Array.isArray(group.modifiers)) {
        available.push(...group.modifiers)
      }
    })
  }

  if (!available.length) {
    return []
  }

  return item.selectedModifiers
    .map((modifierId: number) => available.find((modifier: any) => modifier.id === modifierId))
    .filter((modifier: any): modifier is any => Boolean(modifier))
}

const getModifiersWithCount = (item: any): Array<{ modifier: any, count: number }> => {
  if (!item?.selectedModifiers || item.selectedModifiers.length === 0) {
    return []
  }

  const available: any[] = []
  if (Array.isArray(item.modifiers)) {
    available.push(...item.modifiers)
  }
  if (Array.isArray(item.modifier_groups)) {
    item.modifier_groups.forEach((group: any) => {
      if (Array.isArray(group.modifiers)) {
        available.push(...group.modifiers)
      }
    })
  }

  if (!available.length) {
    return []
  }

  // Группируем модификаторы по ID и считаем количество
  const modifierCountMap = new Map<number, { modifier: any, count: number }>()

  item.selectedModifiers.forEach((modifierId: number) => {
    const modifier = available.find((m: any) => m.id === modifierId)
    if (modifier) {
      const existing = modifierCountMap.get(modifierId)
      if (existing) {
        existing.count += 1
      } else {
        modifierCountMap.set(modifierId, { modifier, count: 1 })
      }
    }
  })

  return Array.from(modifierCountMap.values())
}

const getItemDisplayName = (item: any): string => {
  return item.internal_name && item.internal_name.length
    ? item.internal_name
    : item.our_name && item.our_name.length
      ? item.our_name
      : item.name
}

const formatItemWithModifiers = (item: any): string => {
  const baseName = getItemDisplayName(item)
  const selectedIds: number[] = Array.isArray(item.selectedModifiers) ? item.selectedModifiers : []
  if (!selectedIds.length) {
    return baseName
  }
  const modifiers = getModifiers(item).filter((mod: any) => selectedIds.includes(mod.id))
  if (!modifiers.length) {
    return baseName
  }
  const modifiersText = modifiers.map((m: any) => m.name).join(', ')
  return `${baseName} (${modifiersText})`
}

const buildProductsPayload = (): ICreateOrderProductPayload[] => {
  return menuModel.cart
    .filter((item: any) => item?.external_id && item?.inCart)
    .map((item: any) => {
      const productPayload: ICreateOrderProductPayload = {
        external_id: item.external_id,
        count: item.inCart,
        name: getItemDisplayName(item)
      }

      // Получаем выбранный размер из menuModel для этого продукта
      const selectedSizeId = menuModel.getSelectedSize(item.id)
      if (selectedSizeId) {
        productPayload.size_id = selectedSizeId
      }

      // Группируем модификаторы по external_id и modifiers_group_id для подсчета количества
      const modifierCountMap = new Map<string, { modifier: any, count: number }>()
      let modifiersTotalPrice = 0
      const modifiers = getModifiers(item)
      modifiers.forEach((modifier: any) => {
        if (!modifier?.external_id) {
          return
        }
        // Создаем уникальный ключ для группировки: external_id + modifiers_group_id (если есть)
        const groupId = modifier.modifiers_group_guid || modifier.modifiers_group_id || ''
        const key = `${modifier.external_id}_${groupId}`
        const existing = modifierCountMap.get(key)
        if (existing) {
          existing.count += 1
        } else {
          modifierCountMap.set(key, { modifier, count: 1 })
        }
        modifiersTotalPrice += Number(modifier.price) || 0
      })

      const modifiersPayload: ICreateOrderModifierPayload[] = []
      // Собираем уникальные модификаторы с их количествами
      modifierCountMap.forEach(({ modifier, count }) => {
        const modifierPayload: ICreateOrderModifierPayload = {
          external_id: modifier.external_id,
          count,
          name: modifier.name,
          price: modifier.price
        }

        const groupId = modifier.modifiers_group_guid || modifier.modifiers_group_id
        if (groupId) {
          modifierPayload.modifiers_group_id = groupId
        }

        modifiersPayload.push(modifierPayload)
      })

      if (modifiersPayload.length > 0) {
        productPayload.modifiers = modifiersPayload
      }

      const basePrice = Math.max((Number(item.price) || 0) - modifiersTotalPrice, 0)
      productPayload.price = basePrice

      return productPayload
    })
}

const extractErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object') {
    const err = error as any
    return (
      err?.response?.data?.result?.message ||
      err?.response?.data?.result?.error ||
      err?.message ||
      DEFAULT_ERROR_MESSAGE
    )
  }

  if (typeof error === 'string') {
    return error
  }

  return DEFAULT_ERROR_MESSAGE
}

const waitForMinimumDuration = async (startedAt: number) => {
  const elapsed = Date.now() - startedAt
  if (elapsed < MIN_PROCESSING_DURATION) {
    await new Promise(resolve => setTimeout(resolve, MIN_PROCESSING_DURATION - elapsed))
  }
}

const resolveGuestId = (): number | null => {
  const guest = metaModel.guest
  if (guest === null || guest === undefined || guest === '') {
    return null
  }
  const guestNumber = Number(guest)
  return Number.isNaN(guestNumber) ? null : guestNumber
}

const getBrowserId = (): string => {
  const fingerprint = browserModel.browserId?.find(i => i.plugin === 'fingerprint')
  return fingerprint?.browserId || ''
}

const refreshOrder = async () => {
  const tableId = restaurantModel.tableCode || route.value.params.id
  if (!tableId || !restaurantModel.code) {
    throw new Error('Не удалось обновить заказ. Попробуйте позже.')
  }

  await orderModel.fetchOrder({
    tableId: route.value.params.id,
    domen: restaurantModel.code,
    session: metaModel.session,
    guest: resolveGuestId(),
    browserId: getBrowserId()
  })
}

const captureSubmittedCart = (cartSnapshot: any[]) => {
  submittedCart.value = cartSnapshot.map((item: any) => ({
    ...item,
    selectedModifiers: item.selectedModifiers ? [...item.selectedModifiers] : [],
    modifiers: item.modifiers
      ? item.modifiers.map((modifier: any) => ({ ...modifier }))
      : []
  }))
}

const checkStopList = async (): Promise<boolean> => {
  if (!restaurantModel.restId) {
    return false
  }

  try {
    const response = await getMenuStopList(restaurantModel.restId)
    // Обрабатываем формат ответа API: { "success": true, "stop-list": [...] }
    const responseData = response?.data || response
    const stopListArray = responseData?.['stop-list']
      ? responseData?.['stop-list']
      : responseData?.stopList
        ? responseData?.stopList
        : []

    if (!Array.isArray(stopListArray) || stopListArray.length === 0) {
      menuModel.unavailableDishIds.clear()
      return false
    }

    // Создаем мапы для быстрого поиска
    const stopListProductIds = new Set<number>()
    const stopListExternalProductIds = new Set<string>()
    const stopListModifierIds = new Map<number, Set<number>>() // product_id -> Set<modifier_id>
    const stopListExternalModifierIds = new Map<string, Set<string>>() // external_product_id -> Set<external_modifier_id>
    const stopListGlobalExternalModifierIds = new Set<string>() // Глобальный список всех external_modifier_id в стоп-листе
    const stopListGlobalModifierIds = new Set<number>() // Глобальный список всех modifier_id в стоп-листе (для проверки по внутреннему ID)

    stopListArray.forEach((item: any) => {
      if (item.is_stop_list) {
        // Продукт полностью в стоп-листе
        if (item.product_id) {
          stopListProductIds.add(Number(item.product_id))
        }
        if (item.external_product_id) {
          stopListExternalProductIds.add(String(item.external_product_id))
        }
      }

      // Проверяем модификаторы в стоп-листе
      if (item.modifiers && Array.isArray(item.modifiers) && item.modifiers.length > 0) {
        const productId = item.product_id
        const externalProductId = item.external_product_id

        // Проверка по product_id (внутренний ID)
        if (productId) {
          if (!stopListModifierIds.has(productId)) {
            stopListModifierIds.set(productId, new Set())
          }
          item.modifiers.forEach((modifier: any) => {
            if (modifier.is_stop_list && modifier.modifier_id) {
              const modifierId = Number(modifier.modifier_id)
              stopListModifierIds.get(productId)!.add(modifierId)
              stopListGlobalModifierIds.add(modifierId) // Добавляем в глобальный список
            }
          })
        }

        // Проверка по external_product_id (внешний ID)
        // Если external_product_id есть, добавляем модификаторы в stopListExternalModifierIds
        if (externalProductId) {
          if (!stopListExternalModifierIds.has(String(externalProductId))) {
            stopListExternalModifierIds.set(String(externalProductId), new Set())
          }
          item.modifiers.forEach((modifier: any) => {
            if (modifier.is_stop_list) {
              // Для обычных модификаторов может быть external_product_id вместо external_modifier_id
              if (modifier.external_modifier_id) {
                const externalModifierId = String(modifier.external_modifier_id)
                stopListExternalModifierIds.get(String(externalProductId))!.add(externalModifierId)
                stopListGlobalExternalModifierIds.add(externalModifierId) // Добавляем в глобальный список
                // Также добавляем modifier_id в глобальный список, если он есть
                if (modifier.modifier_id) {
                  stopListGlobalModifierIds.add(Number(modifier.modifier_id))
                }
              } else if (modifier.external_product_id) {
                // Для обычных модификаторов используем external_product_id как идентификатор
                const modifierExternalProductId = String(modifier.external_product_id)
                stopListExternalModifierIds.get(String(externalProductId))!.add(modifierExternalProductId)
                stopListGlobalExternalModifierIds.add(modifierExternalProductId) // Добавляем в глобальный список
                // Также добавляем modifier_id в глобальный список, если он есть
                if (modifier.modifier_id) {
                  stopListGlobalModifierIds.add(Number(modifier.modifier_id))
                }
              }
            }
          })
        } else {
          // Если external_product_id равен null, добавляем модификаторы в глобальный список
          item.modifiers.forEach((modifier: any) => {
            if (modifier.is_stop_list) {
              // Для обычных модификаторов может быть external_product_id вместо external_modifier_id
              if (modifier.external_modifier_id) {
                const externalModifierId = String(modifier.external_modifier_id)
                stopListGlobalExternalModifierIds.add(externalModifierId)
                // Также добавляем modifier_id в глобальный список, если он есть
                if (modifier.modifier_id) {
                  stopListGlobalModifierIds.add(Number(modifier.modifier_id))
                }
              } else if (modifier.external_product_id) {
                // Для обычных модификаторов используем external_product_id как идентификатор
                const modifierExternalProductId = String(modifier.external_product_id)
                stopListGlobalExternalModifierIds.add(modifierExternalProductId)
                // Также добавляем modifier_id в глобальный список, если он есть
                if (modifier.modifier_id) {
                  stopListGlobalModifierIds.add(Number(modifier.modifier_id))
                }
              }
            }
          })
        }
      }
    })

    // Сохраняем актуальный стоп-лист в store, чтобы детальная модалка сразу подсвечивала недоступные модификаторы
    menuModel.stopListModifierIds = stopListModifierIds
    menuModel.stopListExternalModifierIds = stopListExternalModifierIds
    menuModel.stopListGlobalExternalModifierIds = stopListGlobalExternalModifierIds
    menuModel.stopListExternalProductIds = stopListExternalProductIds

    // Проверяем, какие блюда из корзины есть в стоп-листе
    const unavailable: any[] = []
    const unavailableIds = new Set<number>()
    const unavailableModifiersMap = new Map<string, Set<number>>() // cartItemId -> Set<modifierId>

    menuModel.cart.forEach((item: any) => {
      let isUnavailable = false
      const cartItemId = item.cartItemId || String(item.id)
      const unavailableModifierIdsForItem = new Set<number>()

      // Проверяем по product_id
      if (item.product_id && stopListProductIds.has(Number(item.product_id))) {
        isUnavailable = true
      }

      // Проверяем по id (внутренний ID)
      if (item.id && stopListProductIds.has(Number(item.id))) {
        isUnavailable = true
      }

      // Проверяем по external_id
      if (item.external_id && stopListExternalProductIds.has(String(item.external_id))) {
        isUnavailable = true
      }

      if (!isUnavailable && item.selectedModifiers && Array.isArray(item.selectedModifiers) && item.selectedModifiers.length > 0) {
        // Получаем модификаторы из меню (чтобы получить актуальный external_id)
        let productFromMenu: any = null
        menuModel.menuUser.forEach((category: any) => {
          const found = category.products.find((p: any) => p.id === item.id)
          if (found) {
            productFromMenu = found
          }
        })

        // Получаем модификаторы из меню (приоритет) или из элемента корзины (fallback)
        const availableModifiers: any[] = []
        if (productFromMenu) {
          // Получаем модификаторы из меню
          if (Array.isArray(productFromMenu.modifiers)) {
            availableModifiers.push(...productFromMenu.modifiers)
          }
          if (Array.isArray(productFromMenu.modifier_groups)) {
            productFromMenu.modifier_groups.forEach((group: any) => {
              if (Array.isArray(group.modifiers)) {
                availableModifiers.push(...group.modifiers)
              }
            })
          }
        } else {
          // Fallback: получаем модификаторы из элемента корзины
          if (Array.isArray(item.modifiers)) {
            availableModifiers.push(...item.modifiers)
          }
          if (Array.isArray(item.modifier_groups)) {
            item.modifier_groups.forEach((group: any) => {
              if (Array.isArray(group.modifiers)) {
                availableModifiers.push(...group.modifiers)
              }
            })
          }
        }

        // Проверяем модификаторы в стоп-листе по product_id (внутренний ID)
        // Проверяем и по product_id, и по id элемента корзины
        const productId = item.product_id || item.id
        const itemId = item.id

        // Проверка по product_id элемента корзины
        if (productId && stopListModifierIds.has(Number(productId))) {
          const stopListModifierIdsForProduct = stopListModifierIds.get(Number(productId))!
          item.selectedModifiers.forEach((modifierId: number) => {
            if (stopListModifierIdsForProduct.has(Number(modifierId))) {
              unavailableModifierIdsForItem.add(Number(modifierId))
            }
          })
        }

        // Проверка по id элемента корзины (если отличается от product_id)
        if (itemId && itemId !== productId && stopListModifierIds.has(Number(itemId))) {
          const stopListModifierIdsForItem = stopListModifierIds.get(Number(itemId))!
          item.selectedModifiers.forEach((modifierId: number) => {
            if (stopListModifierIdsForItem.has(Number(modifierId))) {
              unavailableModifierIdsForItem.add(Number(modifierId))
            }
          })
        }

        // Проверяем модификаторы в стоп-листе по external_product_id (внешний ID)
        // Сравниваем external_modifier_id из стоп-листа с external_id модификатора из корзины
        // Проверяем по external_id блюда
        if (item.external_id && stopListExternalModifierIds.has(String(item.external_id))) {
          const unavailableExternalModifierIds = stopListExternalModifierIds.get(String(item.external_id))!
          item.selectedModifiers.forEach((modifierId: number) => {
            const modifier = availableModifiers.find((m: any) => m.id === modifierId)
            if (modifier) {
              // Проверяем по external_id модификатора (должен совпадать с external_modifier_id из стоп-листа)
              const modifierExternalId = String(modifier.external_id || '')
              if (modifierExternalId && unavailableExternalModifierIds.has(modifierExternalId)) {
                unavailableModifierIdsForItem.add(Number(modifierId))
              }
            }
          })
        }

        // Также проверяем по external_product_id модификатора (если он отличается от external_id блюда)
        item.selectedModifiers.forEach((modifierId: number) => {
          const modifier = availableModifiers.find((m: any) => m.id === modifierId)
          if (modifier && modifier.external_product_id) {
            const modifierExternalProductId = String(modifier.external_product_id)
            // Проверяем, есть ли в стоп-листе модификаторы для этого external_product_id
            if (stopListExternalModifierIds.has(modifierExternalProductId)) {
              const unavailableExternalModifierIds = stopListExternalModifierIds.get(modifierExternalProductId)!
              const modifierExternalId = String(modifier.external_id || '')
              if (modifierExternalId && unavailableExternalModifierIds.has(modifierExternalId)) {
                unavailableModifierIdsForItem.add(Number(modifierId))
              }
            }
          }
        })

        // Проверка по глобальному списку модификаторов (для случаев, когда external_product_id = null)
        // Проверяем все модификаторы из корзины на совпадение с external_modifier_id из стоп-листа
        item.selectedModifiers.forEach((modifierId: number) => {
          // Сначала проверяем по внутреннему modifier_id (если есть в глобальном списке)
          if (stopListGlobalModifierIds.has(modifierId)) {
            unavailableModifierIdsForItem.add(Number(modifierId))
            return // Пропускаем дальнейшие проверки для этого модификатора
          }
          const modifier = availableModifiers.find((m: any) => m.id === modifierId)
          if (modifier) {
            const modifierExternalId = String(modifier.external_id || '')
            const modifierExternalProductId = String(modifier.external_product_id || '')
            // Проверяем в глобальном списке по external_id
            if (modifierExternalId && stopListGlobalExternalModifierIds.has(modifierExternalId)) {
              unavailableModifierIdsForItem.add(Number(modifierId))
            }
            // Также проверяем по external_product_id (для обычных модификаторов)
            if (modifierExternalProductId && stopListGlobalExternalModifierIds.has(modifierExternalProductId)) {
              unavailableModifierIdsForItem.add(Number(modifierId))
            }
            // Проверяем, если external_id модификатора совпадает с external_product_id продукта в стоп-листе
            // (для случаев, когда обычный модификатор имеет external_id, совпадающий с external_product_id продукта в стоп-листе)
            if (modifierExternalId && stopListExternalProductIds.has(modifierExternalId)) {
              unavailableModifierIdsForItem.add(Number(modifierId))
            }
            // Также проверяем во всех записях stopListExternalModifierIds (на случай, если external_product_id есть)
            stopListExternalModifierIds.forEach((externalModifierIds) => {
              if (modifierExternalId && externalModifierIds.has(modifierExternalId)) {
                unavailableModifierIdsForItem.add(Number(modifierId))
              }
            })
          }
        })
      }

      // Если блюдо само недоступно, добавляем его в список недоступных
      if (isUnavailable) {
        unavailable.push(item)
        unavailableIds.add(item.id)
      }

      // Сохраняем информацию о недоступных модификаторах (даже если блюдо доступно)
      if (unavailableModifierIdsForItem.size > 0) {
        unavailableModifiersMap.set(cartItemId, unavailableModifierIdsForItem)
      }
    })

    // Обновляем состояние
    menuModel.unavailableDishIds = unavailableIds
    menuModel.unavailableModifierIds = unavailableModifiersMap

    // Сохраняем только блюда, которые полностью в стоп-листе (не с модификаторами)
    unavailableDishesOnly.value = unavailable

    if (unavailable.length > 0 || unavailableModifiersMap.size > 0) {
      unavailableDishes.value = unavailable
      return true
    }

    // Очищаем список недоступных блюд и модификаторов, если их нет
    menuModel.unavailableDishIds.clear()
    menuModel.unavailableModifierIds.clear()

    return false
  } catch (error) {
    // Если запрос не удался, пропускаем проверку и продолжаем
    console.error('Failed to check stop-list:', error)
    return false
  }
}

const handleYes = async () => {
  if (!menuModel.cart.length) {
    notificationModel.toggleNotification({
      status: true,
      text: 'Корзина пуста',
      variant: 'error'
    })
    return
  }

  const tableId = restaurantModel.tableCode || route.value.params.id
  if (!tableId) {
    const message = 'Не удалось определить стол. Попробуйте обновить страницу.'
    orderError.value = message
    notificationModel.toggleNotification({
      status: true,
      text: message,
      variant: 'error'
    })
    return
  }

  if (!restaurantModel.code) {
    const message = 'Не удалось определить ресторан.'
    orderError.value = message
    notificationModel.toggleNotification({
      status: true,
      text: message,
      variant: 'error'
    })
    return
  }

  // Проверяем стоп-лист перед созданием заказа
  const hasUnavailableDishes = await checkStopList()

  // Проверяем наличие недоступных модификаторов
  const hasUnavailableModifiers = menuModel.unavailableModifierIds.size > 0

  // Блокируем создание заказа, если есть недоступные блюда или модификаторы
  if (hasUnavailableDishes || hasUnavailableModifiers) {
    showConfirmModal.value = false
    showStopListModal.value = true
    return
  }

  const products = buildProductsPayload()
  if (!products.length) {
    const message = 'Не удалось собрать состав заказа.'
    orderError.value = message
    notificationModel.toggleNotification({
      status: true,
      text: message,
      variant: 'error'
    })
    return
  }

  orderError.value = ''
  isProcessing.value = true
  orderSuccess.value = false
  const startedAt = Date.now()

  const linkTableId = route.value.params.id
  const payload: ICreateOrderPayload = {
    table_id: Array.isArray(linkTableId) ? linkTableId[0] : linkTableId,
    table_code: restaurantModel.tableCode,
    domen: restaurantModel.code,
    guest: resolveGuestId(),
    products
  }

  try {
    const cartSnapshot = menuModel.cart.map(item => ({ ...item }))
    const response = await createOrderByTable(payload)
    if (response?.success) {
      await waitForMinimumDuration(startedAt)
      captureSubmittedCart(cartSnapshot)
      await refreshOrder()
      orderSuccess.value = true
      shouldClearCartOnTransition.value = true
      if (typeof window !== 'undefined' && (window as any).ym) {
        (window as any).ym(process.env.YA_METRIK_ID, 'reachGoal', 'orderPlaced')
      }
    } else {
      throw new Error(response?.result?.message || response?.result?.error || DEFAULT_ERROR_MESSAGE)
    }
  } catch (error) {
    const message = extractErrorMessage(error)
    orderError.value = message
    notificationModel.toggleNotification({
      status: true,
      text: message,
      variant: 'error'
    })
    await waitForMinimumDuration(startedAt)
    orderSuccess.value = false
  } finally {
    isProcessing.value = false
  }
}

const handleNo = () => {
  showConfirmModal.value = false
  isProcessing.value = false
  orderSuccess.value = false
  orderError.value = ''
  submittedCart.value = []
}

const handleGoToPayment = () => {
  if (shouldClearCartOnTransition.value) {
    menuModel.clearCart()
    shouldClearCartOnTransition.value = false
  }
  showConfirmModal.value = false
  orderSuccess.value = false
  orderError.value = ''
  submittedCart.value = []
  emit('goToOrder')
}

const handleCloseModal = () => {
  if (isProcessing.value) {
    return
  }
  if (shouldClearCartOnTransition.value) {
    menuModel.clearCart()
    shouldClearCartOnTransition.value = false
  }
  showConfirmModal.value = false
  orderSuccess.value = false
  orderError.value = ''
  submittedCart.value = []
  showStopListModal.value = false
}

const handleCloseStopListModal = () => {
  // Удаляем блюда, которые полностью в стоп-листе, из корзины
  unavailableDishesOnly.value.forEach((item: any) => {
    const cartItemId = item.cartItemId || String(item.id)
    const count = item.inCart || 0

    // Удаляем все экземпляры этого блюда из корзины
    for (let i = 0; i < count; i++) {
      menuModel.addInCart({
        id: item.id,
        operator: 'minus',
        restName: restaurantModel.code,
        cartItemId
      })
    }
  })

  // Очищаем списки недоступных блюд
  unavailableDishesOnly.value = []
  unavailableDishes.value = []
  menuModel.unavailableDishIds.clear()

  showStopListModal.value = false
}

return { __sfc: true,menuModel, restaurantModel, metaModel, notificationModel, orderModel, browserModel, route, emit, showConfirmModal, isProcessing, orderSuccess, orderError, submittedCart, shouldClearCartOnTransition, showStopListModal, unavailableDishes, unavailableDishesOnly, numberFormatter, getDishWord, cartDishCount, cartTotalPrice, cartSummaryText, DEFAULT_ERROR_MESSAGE, MIN_PROCESSING_DURATION, getModifiers, getModifiersWithCount, getItemDisplayName, formatItemWithModifiers, buildProductsPayload, extractErrorMessage, waitForMinimumDuration, resolveGuestId, getBrowserId, refreshOrder, captureSubmittedCart, checkStopList, handleYes, handleNo, handleGoToPayment, handleCloseModal, handleCloseStopListModal, AppButton, ModalBotton }
}

})